import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LangProvider, useLang } from "@/i18n";
import { GalleryProvider } from "@/gallery";
import { Seo } from "@/components/Seo";
import { Navbar, scrollToId } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { ProductionSpaces } from "@/components/ProductionSpaces";
import { WarehouseSpaces } from "@/components/WarehouseSpaces";
import { Services } from "@/components/Services";
import { Gallery } from "@/components/Gallery";
import { Calculator } from "@/components/Calculator";
import { Contact } from "@/components/Contact";
import { Footer } from "@/components/Footer";
import AdminPage from "@/pages/AdminPage";
import ResetPasswordPage from "@/pages/AdminPassword";

function useLenis() {
  useEffect(() => {
    let lenis;
    let raf;
    let cancelled = false;
    import("lenis")
      .then(({ default: Lenis }) => {
        if (cancelled) return;
        lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
        window.__lenis = lenis;
        const loop = (time) => {
          lenis.raf(time);
          raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      lenis?.destroy();
      window.__lenis = undefined;
    };
  }, []);
}

function SiteLayout() {
  useLenis();
  return (
    <LangProvider>
      <GalleryProvider>
        <Seo />
        <div className="bg-ink min-h-screen">
          <Navbar />
          <main>
            <Outlet />
          </main>
          <Footer />
          <Toaster position="bottom-right" theme="dark" toastOptions={{
            style: { background: "#161B26", border: "1px solid #262E3E", color: "#F1F5F9", borderRadius: 0 },
          }} />
        </div>
      </GalleryProvider>
    </LangProvider>
  );
}

function Landing() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = setTimeout(() => scrollToId(hash), 350);
    return () => clearTimeout(id);
  }, [hash]);

  return (
    <>
      <Hero />
      <Marquee />
      <ProductionSpaces />
      <WarehouseSpaces />
      <Services />
      <Calculator />
      <Contact />
    </>
  );
}

function GalleryPage() {
  const { t } = useLang();
  useEffect(() => {
    const prev = document.title;
    document.title = `${t("gal_title")} | ZTS F-Invest s.r.o.`;
    return () => { document.title = prev; };
  }, [t]);
  return <Gallery />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<SiteLayout />}>
          <Route path="/" element={<Landing />} />
          <Route path="/galeria" element={<GalleryPage />} />
        </Route>
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/reset" element={<ResetPasswordPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
